
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronRight, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { useFilteredInsights } from "@/hooks/useFilteredInsights";
import { useInsightCardsRealtime } from "@/hooks/useInsightCardsRealtime";
import { InsightCard as InsightCardType } from "@/types/insight";

const NationalSentimentDashboard = () => {
  const [topInsights, setTopInsights] = useState<InsightCardType[]>([]);
  const { insights: fetchedInsights, isLoading, error } = useFilteredInsights(null, "KY", 5);
  
  // Setup realtime updates for insights
  useInsightCardsRealtime(setTopInsights);
  
  useEffect(() => {
    if (fetchedInsights && fetchedInsights.length > 0) {
      // Transform the fetched insights to match InsightCardType
      const transformedInsights: InsightCardType[] = fetchedInsights.map(insight => ({
        id: insight.id,
        title: insight.title,
        category: insight.category?.name || '',
        agreePercentage: insight.agree_percentage || 0,
        totalPollsters: insight.total_pollsters || 0,
        description: insight.content || '',
        status: insight.published ? 'published' : 'draft',
        country_code: insight.country_code,
        constituency_code: null,
        linked_poll: insight.linked_poll,
        stats_status: insight.stats_status,
        slug: insight.slug || null
      }));
      
      // Sort by agree percentage and then by total pollsters
      const sorted = transformedInsights.sort((a, b) => {
        if (b.agreePercentage !== a.agreePercentage) {
          return b.agreePercentage - a.agreePercentage;
        }
        return b.totalPollsters - a.totalPollsters;
      });
      
      setTopInsights(sorted.slice(0, 5));
    }
  }, [fetchedInsights]);

  return (
    <section className="py-16 px-4 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
      <div className="container mx-auto max-w-6xl">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold mb-2">
              Cayman National Sentiment
            </h2>
            <p className="text-muted-foreground">
              Real-time insights on the top issues affecting the Cayman Islands
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center text-nation-purple">
              <Users className="mr-2 h-5 w-5" />
              <span>
                <span className="font-bold">{topInsights.reduce((sum, insight) => sum + insight.totalPollsters, 0)}</span> Verified Responses
              </span>
            </div>
            
            <Button variant="outline" size="sm" className="whitespace-nowrap" asChild>
              <Link to="/cards/KY">
                View All Issues <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array(3).fill(0).map((_, i) => (
              <div key={i} className="h-32 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse"></div>
            ))}
          </div>
        ) : error ? (
          <div className="p-6 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg">
            Error loading sentiment data. Please try again later.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {topInsights.map((insight) => (
              <div 
                key={insight.id}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="inline-flex bg-nation-purple/10 text-nation-purple text-xs px-2 py-1 rounded">
                    {insight.category}
                  </span>
                  <span className={`text-sm font-medium ${
                    insight.agreePercentage >= 70 ? 'text-green-600 dark:text-green-400' : 
                    insight.agreePercentage >= 40 ? 'text-amber-600 dark:text-amber-400' : 
                    'text-red-600 dark:text-red-400'
                  }`}>
                    {insight.agreePercentage}% Agreement
                  </span>
                </div>

                <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                  {insight.title}
                </h3>
                
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-4">
                  <div 
                    className={`h-2.5 rounded-full ${
                      insight.agreePercentage >= 70 ? 'bg-green-500' : 
                      insight.agreePercentage >= 40 ? 'bg-amber-500' : 
                      'bg-red-500'
                    }`}
                    style={{ width: `${insight.agreePercentage}%` }}
                  ></div>
                </div>
                
                <div className="flex justify-between items-center text-xs text-muted-foreground">
                  <span>{insight.totalPollsters} responses</span>
                  <Link 
                    to={`/insight/${insight.slug || insight.id}`}
                    className="text-nation-purple hover:text-nation-purple-dark"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))}
            
            {topInsights.length === 0 && !isLoading && (
              <div className="col-span-full text-center p-8 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <p>No sentiment data available yet. Check back soon!</p>
                <Button variant="outline" className="mt-4" asChild>
                  <Link to="/cards/KY">Explore Cayman Cards</Link>
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default NationalSentimentDashboard;
